import Metadata from '../models/Metadata';
import Base64Encoder from '@/util/base64encoder';
import Upload from '@/models/Upload';
import UploadAdapter from '@/adapters/upload';
import Chunking from './chunking';
import FileAES from '@/util/fileAES';
import {default as KeyUtility} from "@/util/keyUtility";
import wait from "./wait";
import * as Sentry from "@sentry/browser";
import store from '@/store';
import AccountDataAES from "@/util/accountDataAES";
import featureFlags from "@/util/featureFlags";
import EncryptedFile from "@/models/EncryptedFile";
import PricingCalculator from "@/util/pricingCalculator";
import TransferInteractionConfiguration from "@/models/TransferInteractionConfiguration";

export default class FileUpload {

  private id!: string;
  private metaKey!: CryptoKey;
  private fileKey!: CryptoKey;
  public files: File[] = [];
  private interactionconfig: TransferInteractionConfiguration;
  private ivs: Uint8Array[] = [];
  private static maxtriesForUpload: number = 3;
  private filesToUpload: EncryptedFile[] = [];
  public expirationDate: Date;

    constructor(files: File[], expirationDate: Date, interactionConfig: TransferInteractionConfiguration, metaKey: CryptoKey) {
        this.files = files;
        this.expirationDate = expirationDate;
        this.metaKey = metaKey;
        this.interactionconfig = interactionConfig;
    }

  /**
   * generates the upload id sets it in the sent req object. Also creates corresponding dirs on the
   * server
   * @returns {Promise} which resolves to the sent object itself
   */
  public async startUpload(usePassword: boolean, password: string, passwordSalt: Uint8Array | null, letterboxAddress: string | null): Promise<void> {
      try {
          // First: Gather Metadata:
          const meta: Metadata = new Metadata();
          meta.filename = ""; //Legacy, can be removed a month after these changes have gone live.
          meta.filesize = 0; //Indicates that all files are dicated by a list
          meta.chunks = 0; //Indicates these are instead delivered by a sorted list!
          meta.chunkIds = []; //Legacy, can be removed a month after these changes have gone live.
          meta.interactionConfiguration = this.interactionconfig; // set config
          //Generate a key for the interactions (regardless if we need it):
          if (meta.interactionConfiguration) {
            const interactionKey = await KeyUtility.generateRandomKey();
            meta.interactionConfiguration.symmetricInteractionKey = await KeyUtility.exportKeyToBase64(interactionKey);
          }
          this.fileKey = await KeyUtility.generateRandomKey();
          meta.keyAsBase64 = await KeyUtility.exportKeyToBase64(this.fileKey);
          if (passwordSalt && passwordSalt.length > 0) {
              meta.saltAsBase64 = Base64Encoder.bytesToString(passwordSalt);
          }

          //Create description objects for files:
          for (const file of this.files) {
            //Main data:
            const encFile: EncryptedFile = new EncryptedFile();
            encFile.name = file.name;
            encFile.size = file.size;
            //Generate Chunk ids and ivs:
            const fileChunks = new Chunking(file.size);
            for (const chunkId of fileChunks.generateOrGetChunkIds()) {
              const bytes = KeyUtility.generateIV();
              encFile.ivs.push(Base64Encoder.bytesToString(bytes));
              encFile.chunks.push(chunkId);
            }

            //Push to metadata and also to our to do list:
            meta.files.push(encFile);
            this.filesToUpload.push(encFile);
          }

          // Encrypt Metadata:
          const metaIV = KeyUtility.generateIV();

          const encryptedMetadata = await meta.getEncryptedAsBase64(this.metaKey, metaIV);

          // Create an Upload and send it to the Server to get back the id:
          const upload: Upload = new Upload();
          upload.iv = Base64Encoder.bytesToString(metaIV);
          upload.encryptedMetadata = encryptedMetadata;
          upload.passwordProtected = usePassword;
          upload.expiry = PricingCalculator.getFixedExpiryDate(this.expirationDate);

          if (store.getters.isLoggedIn && store.state.account.accountData) {
              //Encrypt link with account key:
              const accountEncryption: AccountDataAES = await AccountDataAES.fromAccountData(store.state.account.masterKey, store.state.account.accountData);
              const secretIv = AccountDataAES.generateIV();
              const metaKeyAsB64 = await AccountDataAES.exportKeyToBase64(this.metaKey);
              upload.secretEncrypted = await accountEncryption.encryptStringAndConvertToBase64(metaKeyAsB64, secretIv);
              upload.secretEncryptedIv = Base64Encoder.bytesToString(secretIv);
          }

          const adapter: UploadAdapter = new UploadAdapter();
          const uploadFromBackend = await adapter.startUpload(upload, letterboxAddress);
          this.id = uploadFromBackend.id;

          return;
      } catch (e) {
          Sentry.captureException(e);
          throw e;
      }
  }

  //TODO: Check where this is called to adapt callback function.
  public async uploadChunks(progressCallback: (fileName: string, filePercent: number, totalPercent: number, progressEvent: ProgressEvent) => void):
      Promise<boolean> {
      const id: string = this.id;
      if (!id) {
          return false;
      }

      let allChunkCount: number = 0;
      this.filesToUpload.forEach((encFile: EncryptedFile) => {
          allChunkCount += encFile.chunks.length;
      });

      const uploadTotalPercent: number = 100 * allChunkCount;

      const adapter = new UploadAdapter();

      for (let fileIdx = 0; fileIdx < this.filesToUpload.length; fileIdx++) {
        const encFile = this.filesToUpload[fileIdx];
        const chunking = new Chunking(encFile.size);
        const fileTotalPercent: number = 100 * encFile.chunks.length;

        for (let chunkIdx = 0; chunkIdx < encFile.chunks.length; chunkIdx++) {
          let tries = 0;
          let success = false;

          while (!success && tries < FileUpload.maxtriesForUpload) {

            try {
              const beginning = chunking.getChunkBeginning(chunkIdx);
              const end = chunking.getChunkEnd(chunkIdx);
              const chunkBytes = await FileUpload.readBufferFromFileRange(this.files[fileIdx], beginning, end);
              const aes = new FileAES(this.fileKey);
              const iv: Uint8Array = Base64Encoder.stringToBytes(encFile.ivs[chunkIdx]);
              const encrypted = await aes.encryptChunk(chunkBytes, iv);
              await adapter.uploadChunk(this.id, encFile.chunks[chunkIdx], encrypted,
                (progressEvent: ProgressEvent) => {
                  const percentOfThisChunk: number = (progressEvent.loaded / progressEvent.total) * 100;
                  const percentOfThisFile: number = ((chunkIdx * 100 + percentOfThisChunk) / fileTotalPercent) * 100 ;
                  const percentDone = ((fileIdx * 100 + percentOfThisFile) / uploadTotalPercent) * 100 ;
                  progressCallback(encFile.name, percentOfThisFile, percentDone, progressEvent);
                });
              success = true;
            } catch (e) {
              console.warn(
                `Chunk ${encFile.chunks[chunkIdx]}: Upload failed; Try ${tries} of ${FileUpload.maxtriesForUpload}`, e);
              Sentry.captureException(e);
              await wait(1000); //give everything a little time to work again magically
            } finally {
              tries++;
            }
          }

          if (!success) {
            throw new Error(`Fehler beim Hochladen der Datei: Teil ${chunkIdx}` +
              ` konnte nicht heruntergeladen werden (${tries} Versuche).`);
          }
        }
      }
      return true;
  }

  public async finishUpload(): Promise<string> {
    const id = this.id;
    if (!id) {
      return '';
    }

    try {
        const adapter = new UploadAdapter();
        await adapter.commitUpload(id);
        const link: string = featureFlags.frontendBaseUrl + "/download/" + this.id;
        return link;
    } catch (e) {
        Sentry.captureException(e);
        throw e;
    }

  }

  private static async readBufferFromFileRange(file: File, start: number, end: number): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const range = file.slice(start, end + 1); // +1 since end param is exclusive
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        resolve(fileReader.result as ArrayBuffer);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
      fileReader.readAsArrayBuffer(range);
    });
  }
}
